import { useMemo } from 'react';
import styled from 'styled-components';
import { api, BracketStage, Match } from '../api/client';
import { useAsync, Async } from '../components/Async';
import { Flag } from '../components/TeamBadge';
import { Muted } from '../components/ui';
import { teamName } from '../lib/flags';
import { useStore } from '../stores/store';
import {
  buildGraph,
  createResolver,
  isLocked,
  pickWinner,
  unpickWinner,
  decidedCount,
  bracketHalf,
  Slot,
} from '../lib/simulate';

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius};
  padding: 13px 15px;
  margin-bottom: 12px;
  font-size: 1.05rem;
  .grow { flex: 1; }
  .grow strong { font-size: 1.15rem; }
  button {
    background: ${({ theme }) => theme.colors.surfaceAlt};
    color: ${({ theme }) => theme.colors.text};
    border: 1px solid ${({ theme }) => theme.colors.borderStrong};
    border-radius: 999px;
    padding: 6px 12px;
    font-size: 0.8rem;
    min-height: auto;
  }
`;

const Champion = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.gold}, #b9820a);
  color: #1a1300;
  border-radius: ${({ theme }) => theme.radius};
  padding: 14px;
  margin-bottom: 12px;
  font-weight: 800;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.1rem;
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  align-items: start;
`;

const ColHead = styled(TwoCol)`
  margin-bottom: 6px;
  span {
    text-align: center;
    font-weight: 800;
    font-size: 0.8rem;
    padding: 5px;
    border-radius: 8px;
    background: ${({ theme }) => theme.colors.surfaceAlt};
  }
  .a { color: ${({ theme }) => theme.colors.accent}; }
  .b { color: ${({ theme }) => theme.colors.gold}; }
`;

const RoundLabel = styled.div`
  text-align: center;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.textMuted};
  margin: 14px 0 6px;
`;

const MatchBox = styled.div<{ $col: 'a' | 'b' | 'center' }>`
  background: ${({ theme }) => theme.colors.surface};
  border: 2px solid
    ${({ theme, $col }) =>
      $col === 'a' ? theme.colors.accent : $col === 'b' ? theme.colors.gold : theme.colors.borderStrong};
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 8px;
`;

const SideRow = styled.button<{ $win: boolean; $loser: boolean; $fav: boolean; $pickable: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  text-align: left;
  border: none;
  border-left: 4px solid ${({ theme, $fav }) => ($fav ? theme.colors.gold : 'transparent')};
  background: ${({ theme, $win }) => ($win ? theme.colors.accent : 'transparent')};
  color: ${({ theme, $win }) => ($win ? theme.colors.accentText : theme.colors.text)};
  opacity: ${({ $loser }) => ($loser ? 0.4 : 1)};
  cursor: ${({ $pickable }) => ($pickable ? 'pointer' : 'default')};
  &:first-child { border-bottom: 1px solid ${({ theme }) => theme.colors.border}; }

  .nm { flex: 1; font-weight: 600; font-size: 0.98rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .muted { color: ${({ theme }) => theme.colors.textMuted}; font-style: italic; font-weight: 500; }
  .check { font-weight: 800; }
`;

function Side({
  slot,
  isWinner,
  isLoser,
  favorite,
  pickable,
  onPick,
}: {
  slot: Slot;
  isWinner: boolean;
  isLoser: boolean;
  favorite: string | null;
  pickable: boolean;
  onPick: () => void;
}) {
  const known = slot.team !== null;
  return (
    <SideRow
      $win={isWinner}
      $loser={isLoser}
      $fav={known && slot.team === favorite}
      $pickable={pickable && known}
      onClick={() => pickable && known && onPick()}
    >
      <Flag team={slot.team} size={18} />
      <span className={known ? 'nm' : 'nm muted'}>
        {known ? teamName(slot.team as string) : slot.label}
        {slot.projected && known ? ' ●' : ''}
      </span>
      {isWinner && <span className="check">✓</span>}
    </SideRow>
  );
}

const SPLIT_ROUNDS = ['r32', 'r16', 'qf', 'sf'];
const CENTER_ROUNDS = ['third', 'final'];

function BracketSim({ stages }: { stages: BracketStage[] }) {
  const favorite = useStore((s) => s.favorite);
  const picks = useStore((s) => s.bracketPicks);
  const setBracketPicks = useStore((s) => s.setBracketPicks);
  const resetBracket = useStore((s) => s.resetBracket);

  const g = useMemo(() => buildGraph(stages), [stages]);
  const r = createResolver(g, picks);

  const champion = r.winner(104);
  const decided = decidedCount(g, picks);

  const handlePick = (m: Match, team: string) => {
    if (isLocked(m)) return;
    const num = m.num as number;
    setBracketPicks(picks[num] === team ? unpickWinner(num, g, picks) : pickWinner(num, team, g, picks));
  };

  const renderMatch = (m: Match, col: 'a' | 'b' | 'center') => {
    const [s1, s2] = r.parts(m);
    const win = r.winner(m.num as number);
    const locked = isLocked(m);
    return (
      <MatchBox key={m.id} $col={col}>
        <Side
          slot={s1}
          isWinner={win !== null && win === s1.team}
          isLoser={win !== null && s1.team !== null && win !== s1.team}
          favorite={favorite}
          pickable={!locked}
          onPick={() => s1.team && handlePick(m, s1.team)}
        />
        <Side
          slot={s2}
          isWinner={win !== null && win === s2.team}
          isLoser={win !== null && s2.team !== null && win !== s2.team}
          favorite={favorite}
          pickable={!locked}
          onPick={() => s2.team && handlePick(m, s2.team)}
        />
      </MatchBox>
    );
  };

  const stageBy = (key: string) => stages.find((s) => s.key === key);

  return (
    <>
      <Bar>
        <span className="grow">
          🎮 <strong>Simulá la llave</strong> · {decided}/{g.matches.length} definidos
        </span>
        <button onClick={resetBracket}>Reiniciar</button>
      </Bar>

      {champion && (
        <Champion>
          🏆 <Flag team={champion} size={30} /> Campeón: {teamName(champion)}
        </Champion>
      )}

      <Muted style={{ margin: '0 0 14px', fontSize: '1rem', lineHeight: 1.55 }}>
        Los cruces están armados con <strong>las posiciones actuales de los grupos</strong> (●) y se
        van ajustando solos a medida que se juegan los partidos. Tocá quién pasa en cada llave para
        armar tu propio cuadro; tocá de nuevo al ganador para deshacer. Se guarda en este dispositivo.
      </Muted>

      <ColHead>
        <span className="a">🟢 Llave A</span>
        <span className="b">🟡 Llave B</span>
      </ColHead>

      {SPLIT_ROUNDS.map((key) => {
        const st = stageBy(key);
        if (!st) return null;
        const left = st.matches.filter((m) => bracketHalf(m.num as number, g) === 'A');
        const right = st.matches.filter((m) => bracketHalf(m.num as number, g) === 'B');
        return (
          <div key={key}>
            <RoundLabel>{st.label}</RoundLabel>
            <TwoCol>
              <div>{left.map((m) => renderMatch(m, 'a'))}</div>
              <div>{right.map((m) => renderMatch(m, 'b'))}</div>
            </TwoCol>
          </div>
        );
      })}

      {CENTER_ROUNDS.map((key) => {
        const st = stageBy(key);
        if (!st) return null;
        return (
          <div key={key}>
            <RoundLabel>{key === 'final' ? '🏆 ' : ''}{st.label}</RoundLabel>
            {st.matches.map((m) => renderMatch(m, 'center'))}
          </div>
        );
      })}
    </>
  );
}

export function Bracket() {
  const state = useAsync(() => api.bracket(), [], '/bracket');
  return <Async state={state}>{(d) => <BracketSim stages={d.stages} />}</Async>;
}
